import React, { useState } from 'react';
import {
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack as MuiStack,
  Typography,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Divider,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Checkbox,
  Paper,
} from '@mui/material';
import PaiButton from 'features/ui/base-components/pai-button';
import PaiIconButton from 'features/ui/base-components/pai-icon-button';
import PaiDropzone from 'features/ui/base-components/pai-dropzone';
import Papa from 'papaparse';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Cancel';
import { EmailAccountTypes, EmailCredentials, OAuthCredentials, LinkedinAccount, EmailAccount, Encryptions } from '../linkedin-accounts.types';
import { createEmailAccount, updateEmailAccount, getEmailAccounts } from 'API/linkedin-accounts';
// import _ from 'lodash';
import { useTranslation } from 'react-i18next';

interface Quality {
  dmarc?: boolean;
  spf?: boolean;
  dkim?: boolean;
  mx?: boolean;
  disposable?: boolean;
}

export interface Props {
  open: boolean;
  mode: 'add' | 'edit';
  account: LinkedinAccount;
  sender: EmailAccount | null;
  onClose(): void;
  onCompleted(): void;
}

enum WizardStep {
  PROVIDER = 0,
  CREDENTIALS = 1,
  SUMMARY = 2,
  VERIFY = 3,
}

const initialEmailCredentials = (): EmailCredentials => ({
  server: '',
  port: '',
  encryption: 'none' as any,
  username: '',
  password: '',
});

const AddEditSenderWizard: React.FC<Props> = ({ open, mode, account, sender, onClose, onCompleted }) => {
  const { t } = useTranslation();

  const [activeStep, setActiveStep] = useState<WizardStep>(WizardStep.PROVIDER);
  const [verificationSuccess, setVerificationSuccess] = useState<boolean | null>(null);
  const [provider, setProvider] = useState<EmailAccountTypes>(sender?.type || EmailAccountTypes.SMTP);

  // Bulk CSV upload state
  const [csvRows, setCsvRows] = useState<any[]>([]);
  const [importSummary, setImportSummary] = useState<{ success: number; failed: number }>({ success: 0, failed: 0 });
  const [rowSelections, setRowSelections] = useState<boolean[]>([]);
  // Bulk processing index
  const [bulkIdx, setBulkIdx] = useState<number>(0);
  // Mapping of internal fields -> CSV headers
  const REQUIRED_FIELDS = [
    'Email',
    'SMTP Host',
    'SMTP Port',
    'SMTP Username',
    'SMTP Password',
    'IMAP Host',
    'IMAP Port',
    'IMAP Username',
    'IMAP Password',
  ];

  const [columnMapping, setColumnMapping] = useState<Record<string, string>>({});

  const mappingComplete = React.useMemo(
    () => REQUIRED_FIELDS.every((field) => columnMapping[field]),
    [columnMapping]
  );

  const [smtp, setSmtp] = useState<EmailCredentials>(
    sender?.type === EmailAccountTypes.SMTP && sender
      ? // If editing, prefill SMTP from sender
      ((sender as unknown) as { smtp?: EmailCredentials }).smtp ?? initialEmailCredentials()
      : initialEmailCredentials()
  );
  const [imap, setImap] = useState<EmailCredentials>(
    sender?.type === EmailAccountTypes.SMTP && sender
      ? ((sender as unknown) as { imap?: EmailCredentials }).imap ?? initialEmailCredentials()
      : initialEmailCredentials()
  );

  const [oauth, setOauth] = useState<OAuthCredentials>({ email: (sender as any)?.username ?? '', code: '' });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [quality, setQuality] = useState<Quality | null>(null);

  const next = () => setActiveStep((s) => (s + 1) as WizardStep);
  const back = () => setActiveStep((s) => (s - 1) as WizardStep);

  const submitCredentials = async () => {
    setLoading(true);
    setError(null);

    console.log('🚀 submitCredentials called');
    console.log('📊 Mode:', mode);
    console.log('📧 Provider:', provider);
    console.log('📄 CSV rows:', csvRows.length);
    console.log('🎯 Intended email:', provider === EmailAccountTypes.SMTP || provider === EmailAccountTypes.OTHER ? smtp.username : oauth.email);

    // Handle bulk import step-by-step
    if (csvRows.length > 0) {
      console.log('📂 Taking BULK CSV path');
      const row = selectedRows[bulkIdx];
      if (!row) {
        setLoading(false);
        return;
      }

      const getVal = (field: string) => row[columnMapping[field] || field] || '';

      // Validate required fields
      const missingFields = REQUIRED_FIELDS.filter(field => !getVal(field));
      if (missingFields.length > 0) {
        setVerificationSuccess(false);
        setError(`Missing required fields in this row: ${missingFields.join(', ')}`);
        setLoading(false);
        return;
      }

      const smtpPort = String(getVal('SMTP Port'));
      let smtpEncryption: Encryptions;
      if (smtpPort === '465') {
        smtpEncryption = Encryptions.SSL;
      } else if (smtpPort === '587') {
        smtpEncryption = Encryptions.STARTTLS;
      } else {
        smtpEncryption = Encryptions.SSL; // Default fallback
      }

      const smtpCreds: EmailCredentials = {
        server: getVal('SMTP Host'),
        port: smtpPort,
        encryption: smtpEncryption,
        username: getVal('SMTP Username') || getVal('Email'),
        password: getVal('SMTP Password'),
      };

      const imapPort = String(getVal('IMAP Port'));
      let imapEncryption: Encryptions;
      if (imapPort === '993') {
        imapEncryption = Encryptions.SSL;
      } else if (imapPort === '143') {
        imapEncryption = Encryptions.STARTTLS;
      } else {
        imapEncryption = Encryptions.SSL; // Default fallback
      }

      const imapCreds: EmailCredentials = {
        server: getVal('IMAP Host'),
        port: imapPort,
        encryption: imapEncryption,
        username: getVal('IMAP Username') || getVal('Email'),
        password: getVal('IMAP Password'),
      };

      const payload: any = { type: EmailAccountTypes.SMTP, smtp: smtpCreds, imap: imapCreds };

      let rowSuccess = false;
      try {
        const intendedEmail = (smtpCreds.username ?? '').toLowerCase();
        const existing = account.emailAccounts?.find((ea) => (ea.username ?? '').toLowerCase() === intendedEmail);

        // If email already exists, skip API call and mark as success
        if (existing) {
          setVerificationSuccess(true);
          rowSuccess = true;
        } else {
          const res: any = await createEmailAccount(account._id, payload);
          if ((res as any)?.success === true || res === true || res?.data === true) {
            setVerificationSuccess(true);
            rowSuccess = true;
            const q = (res as any)?.quality ?? (res as any)?.data?.quality;
            if (q && typeof q === 'object') setQuality(q);
          } else {
            setVerificationSuccess(false);
            // Set error code safely - only use string values
            if (typeof res.error === 'string') {
              setErrorCode(res.error);
            } else if (res.error && typeof res.error === 'object' && res.error.error) {
              setErrorCode(res.error.error);
            } else {
              setErrorCode(null);
            }
            // Handle different error formats safely
            let errorMessage = 'Unknown error occurred';
            if (typeof res.error === 'string') {
              errorMessage = res.error;
            } else if (res.error && typeof res.error === 'object') {
              // If error is an object, try to extract a meaningful message
              if (res.error.message) {
                errorMessage = res.error.message;
              } else if (res.error.error) {
                errorMessage = res.error.error;
              } else {
                errorMessage = 'An error occurred while processing your request';
              }
            } else if (res.message) {
              errorMessage = res.message;
            }
            setError(errorMessage);
            const q = res.quality;
            if (q && typeof q === 'object') setQuality(q);
          }
        }
      } catch (err) {
        const e: any = err;
        setVerificationSuccess(false);
        setErrorCode(e?.message ?? 'Unknown error');
        setError(e?.message ?? 'Unknown error');
        const q = e?.quality;
        if (q && typeof q === 'object') setQuality(q);
      }
      // update summary counts using rowSuccess
      setImportSummary((prev) => ({ success: prev.success + (rowSuccess ? 1 : 0), failed: prev.failed + (rowSuccess ? 0 : 1) }));
      setLoading(false);
      return;
    }

    console.log('🎯 Taking SINGLE ACCOUNT path');

    const payload: any = { type: provider };
    if (provider === EmailAccountTypes.SMTP || provider === EmailAccountTypes.OTHER) {
      payload.smtp = smtp;
      payload.imap = imap;
      console.log('📦 SMTP/OTHER payload:', payload);
    } else if (provider === EmailAccountTypes.GMAIL) {
      payload.gmailOauth = oauth; // Use 'gmailOauth' for new email accounts API
      console.log('📦 GMAIL payload:', payload);
      console.log('🔑 Gmail OAuth details:');
      console.log('  - Email:', oauth.email);
      console.log('  - Code length:', oauth.code ? oauth.code.length : 0);
      console.log('  - Code preview:', oauth.code ? oauth.code.substring(0, 20) + '...' : 'EMPTY');
    } else if (provider === EmailAccountTypes.MS_O365) {
      payload.oauth = oauth;
      console.log('📦 MS_O365 payload:', payload);
    }

    try {
      let res: any;
      const intendedEmail = provider === EmailAccountTypes.SMTP || provider === EmailAccountTypes.OTHER ? smtp.username : oauth.email;
      console.log('🔍 Final intended email for lookup:', intendedEmail);

      if (mode === 'add') {
        console.log('➕ Mode is ADD - checking for existing accounts');
        // CRITICAL FIX: Always fetch fresh email accounts data before checking for existing accounts
        // This prevents using stale state data that might reference deleted accounts
        try {
          const freshEmailAccounts = await getEmailAccounts(account._id);
          console.log('🔍 Fresh email accounts fetched:', freshEmailAccounts);

          // Check if API returned an error (like 401 Unauthorized)
          if (freshEmailAccounts && typeof freshEmailAccounts === 'object' && 'error' in freshEmailAccounts) {
            console.error('🚫 API error fetching email accounts:', freshEmailAccounts.error);
            console.log('➕ Using POST request due to API error');
            res = await createEmailAccount(account._id, payload);
          } else if (Array.isArray(freshEmailAccounts)) {
            console.log('🎯 Looking for email:', intendedEmail.toLowerCase());

            const existing = freshEmailAccounts.find((ea: any) =>
              (ea.username ?? '').toLowerCase() === (intendedEmail ?? '').toLowerCase()
            );

            console.log('📧 Existing account found:', existing);

            if (existing) {
              // Account exists, update it (PUT request)
              console.log('🔄 Using PUT request to update existing account:', existing._id);
              res = await updateEmailAccount(account._id, existing._id, payload);
            } else {
              // Account doesn't exist, create new one (POST request)
              console.log('➕ Using POST request to create new account');
              console.log('📤 Sending payload:', JSON.stringify(payload, null, 2));
              console.log('🌐 Using new email accounts API: POST /linkedinAccounts/{id}/emailAccounts');
              res = await createEmailAccount(account._id, payload);
              console.log('📥 API response:', res);
            }
          } else {
            // Unexpected response format
            console.warn('⚠️ Unexpected response format, defaulting to create:', freshEmailAccounts);
            console.log('📤 Fallback - Sending payload:', JSON.stringify(payload, null, 2));
            res = await createEmailAccount(account._id, payload);
            console.log('📥 Fallback API response:', res);
          }
        } catch (fetchError) {
          // If we can't fetch fresh data, default to create (safer option)
          console.error('❌ Exception fetching fresh email accounts, defaulting to create:', fetchError);
          console.log('📤 Exception fallback - Sending payload:', JSON.stringify(payload, null, 2));
          res = await createEmailAccount(account._id, payload);
          console.log('📥 Exception fallback API response:', res);
        }
      } else if (sender) {
        // Edit mode - use the existing sender ID
        console.log('✏️ Mode is EDIT - using existing sender ID:', sender._id);
        res = await updateEmailAccount(account._id, sender._id, payload);
      } else {
        console.warn('⚠️ Neither ADD nor EDIT mode, this should not happen!');
      }

      if ((res as any)?.success === true || res === true || res?.data === true) {
        setVerificationSuccess(true);
        const q = (res as any)?.quality ?? (res as any)?.data?.quality;
        if (q && typeof q === 'object') setQuality(q);
        setLoading(false);
      } else if (res?.error) {
        setVerificationSuccess(false);
        // Set error code safely - only use string values
        if (typeof res.error === 'string') {
          setErrorCode(res.error);
        } else if (res.error && typeof res.error === 'object' && res.error.error) {
          setErrorCode(res.error.error);
        } else {
          setErrorCode(null);
        }
        // Handle different error formats safely
        let errorMessage = 'Unknown error occurred';
        if (typeof res.error === 'string') {
          errorMessage = res.error;
        } else if (res.error && typeof res.error === 'object') {
          // If error is an object, try to extract a meaningful message
          if (res.error.message) {
            errorMessage = res.error.message;
          } else if (res.error.error) {
            errorMessage = res.error.error;
          } else {
            errorMessage = 'An error occurred while processing your request';
          }
        } else if (res.message) {
          errorMessage = res.message;
        }
        setError(errorMessage);
        const q = res.quality;
        if (q && typeof q === 'object') setQuality(q);
        setLoading(false);
      }
    } catch (e: any) {
      setError(e.message ?? 'Unknown error');
      setLoading(false);
    }
  };

  // Trigger verification when entering VERIFY step
  React.useEffect(() => {
    if (activeStep === WizardStep.VERIFY) {
      submitCredentials();
    }
  }, [activeStep, bulkIdx]);

  const steps = [t('provider'), t('credentials'), t('summary'), t('verification')];

  const handleCsvDrop = React.useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length === 0) return;
    const file = acceptedFiles[0];
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results: any) => {
        setCsvRows(results.data as any[]);
        setRowSelections(new Array(results.data.length).fill(true));
        // Auto-map columns by exact header match (case-insensitive)
        const autoMap: Record<string, string> = {};
        const headers: string[] = results.meta?.fields || Object.keys(results.data[0] || {});
        REQUIRED_FIELDS.forEach((f) => {
          const h = headers.find((hd) => hd.toLowerCase() === f.toLowerCase());
          if (h) autoMap[f] = h;
        });
        setColumnMapping(autoMap);
        setProvider(EmailAccountTypes.SMTP);
        // Skip provider step and go directly to row-selection credentials step
        setActiveStep(WizardStep.CREDENTIALS);
      },
    });
  }, []);

  const headers = React.useMemo(() => (csvRows.length > 0 ? Object.keys(csvRows[0]) : []), [csvRows]);

  const selectedCount = rowSelections.filter(Boolean).length;

  const selectedRows = React.useMemo(() => csvRows.filter((_, idx) => rowSelections[idx]), [csvRows, rowSelections]);

  const bulkMode = csvRows.length > 0;

  const currentRow = bulkMode ? selectedRows[bulkIdx] : null;
  const currentEmail = currentRow ? currentRow[columnMapping['Email'] || 'Email'] : '';

  // Reset bulkIdx when selectedRows change (i.e., user toggled checkboxes)
  React.useEffect(() => {
    setBulkIdx(0);
  }, [selectedRows.length]);

  const allSelected = rowSelections.length > 0 && rowSelections.every(Boolean);

  const toggleRowSelection = (idx: number) => {
    setRowSelections((prev) => prev.map((v, i) => (i === idx ? !v : v)));
  };

  const toggleAllRows = () => {
    const newVal = !allSelected;
    setRowSelections(rowSelections.map(() => newVal));
  };

  // Step 1 – provider selection & optional CSV upload with row preview
  const providerStep = (
    <Stack spacing={2} mt={2}>
      <Typography>{t('chooseProvider')}</Typography>
      <RadioGroup value={provider} onChange={(e) => setProvider(e.target.value as EmailAccountTypes)}>
        <FormControlLabel value={EmailAccountTypes.GMAIL} control={<Radio />} label={t('providerGmail')} />
        <FormControlLabel value={EmailAccountTypes.MS_O365} control={<Radio />} label={t('providerMicrosoft365')} />
        <FormControlLabel value={EmailAccountTypes.SMTP} control={<Radio />} label={t('providerSmtpImap')} />
      </RadioGroup>
      <Typography variant="body2" color="text.secondary">
        {t('providerHint')}
      </Typography>

      {/* CSV upload section */}
      <Divider sx={{ my: 2 }}>{t('orUploadCsv')}</Divider>
      <PaiDropzone onDrop={handleCsvDrop} />

      {csvRows.length > 0 && (
        <>
          <Typography variant="body2" color="success.main">
            {t('csvLoadedSelected', { selected: selectedCount, count: csvRows.length })}
          </Typography>
          <Typography variant="body2" color="text.secondary">{t('selectRowsHint')}</Typography>
          <TableContainer component={Paper} sx={{ maxHeight: 260 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={allSelected}
                      indeterminate={!allSelected && rowSelections.some((v) => v)}
                      onChange={toggleAllRows}
                    />
                  </TableCell>
                  {headers.map((h) => (
                    <TableCell key={h}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {csvRows.slice(0, 10).map((row, idx) => (
                  <TableRow key={idx} hover>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={rowSelections[idx]}
                        onChange={() => toggleRowSelection(idx)}
                      />
                    </TableCell>
                    {headers.map((h) => (
                      <TableCell key={h}>{row[h]}</TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {csvRows.length > 10 && (
            <Typography variant="caption" color="text.secondary">
              {t('tablePreviewHint', { shown: 10, total: csvRows.length })}
            </Typography>
          )}
        </>
      )}
    </Stack>
  );

  // Helper to render instructions text per provider
  const renderProviderInstructions = () => {
    switch (provider) {
      case EmailAccountTypes.GMAIL:
        return t('gmailInstructions');
      case EmailAccountTypes.MS_O365:
        return t('microsoftInstructions');
      case EmailAccountTypes.SMTP:
      default:
        return t('smtpInstructions');
    }
  };

  const smtpCredentialsForm = (
    <Stack spacing={2} mt={2}>
      <Typography variant="body2" color="text.secondary">
        {renderProviderInstructions()}
      </Typography>

      <Typography variant="subtitle2">{t('smtpSection')}</Typography>
      <TextField label={t('fieldServer')} value={smtp.server} onChange={(e) => setSmtp({ ...smtp, server: e.target.value })} fullWidth />
      <TextField label={t('fieldPort')} value={smtp.port} onChange={(e) => setSmtp({ ...smtp, port: e.target.value })} fullWidth />
      <FormControl fullWidth>
        <InputLabel>{t('fieldEncryption')}</InputLabel>
        <Select
          label={t('fieldEncryption')}
          value={smtp.encryption}
          onChange={(e) => setSmtp({ ...smtp, encryption: e.target.value as any })}
        >
          <MenuItem value="none">{t('encryptionNone')}</MenuItem>
          <MenuItem value="ssl">{t('encryptionSSL')}</MenuItem>
          <MenuItem value="tls">{t('encryptionTLS')}</MenuItem>
          <MenuItem value="starttls">{t('encryptionSTARTTLS')}</MenuItem>
        </Select>
      </FormControl>
      <TextField label={t('fieldUsername')} value={smtp.username} onChange={(e) => setSmtp({ ...smtp, username: e.target.value })} fullWidth />
      <TextField label={t('fieldPassword')} type="password" value={smtp.password} onChange={(e) => setSmtp({ ...smtp, password: e.target.value })} fullWidth />

      <Typography variant="subtitle2" mt={2}>{t('imapSection')}</Typography>
      <TextField label={t('fieldServer')} value={imap.server} onChange={(e) => setImap({ ...imap, server: e.target.value })} fullWidth />
      <TextField label={t('fieldPort')} value={imap.port} onChange={(e) => setImap({ ...imap, port: e.target.value })} fullWidth />
      <FormControl fullWidth>
        <InputLabel>{t('fieldEncryption')}</InputLabel>
        <Select
          label={t('fieldEncryption')}
          value={imap.encryption}
          onChange={(e) => setImap({ ...imap, encryption: e.target.value as any })}
        >
          <MenuItem value="none">{t('encryptionNone')}</MenuItem>
          <MenuItem value="ssl">{t('encryptionSSL')}</MenuItem>
          <MenuItem value="tls">{t('encryptionTLS')}</MenuItem>
        </Select>
      </FormControl>
      <TextField label={t('fieldUsername')} value={imap.username} onChange={(e) => setImap({ ...imap, username: e.target.value })} fullWidth />
      <TextField label={t('fieldPassword')} type="password" value={imap.password} onChange={(e) => setImap({ ...imap, password: e.target.value })} fullWidth />
    </Stack>
  );

  // OAuth URLs (same as legacy modal)
  const authMsAccountURL = `https://login.microsoftonline.com/common/oauth2/v2.0/authorize?client_id=22437d1b-8d8e-40f8-a71f-d688a9615269&response_type=code&redirect_uri=${window.location.origin}/dashboard/oauth/microsoft/success&scope=user.read offline_access Mail.Send Mail.read openid&response_mode=query&prompt=consent&hint=${oauth.email}`;

  const authGmailAccountURL = `https://accounts.google.com/o/oauth2/v2/auth?redirect_uri=${window.location.origin}/dashboard/oauth/gmail/success&prompt=consent&response_type=code&client_id=184485567772-d9itn31afp3dl1k75nsqms5uf6feldcp.apps.googleusercontent.com&scope=https%3A%2F%2Fmail.google.com%2F&access_type=offline`;

  const oauthCredentialsForm = (
    <Stack spacing={2} mt={2}>
      <Typography variant="body2" color="text.secondary">
        {renderProviderInstructions()}
      </Typography>

      {/* Row 1 – email */}
      <TextField
        label={t('fieldEmail')}
        value={oauth.email}
        onChange={(e) => setOauth({ ...oauth, email: e.target.value })}
        fullWidth
      />

      {/* Row 2 – authorize button */}
      <PaiButton
        text={provider === EmailAccountTypes.GMAIL ? t('authorizeGmail') : t('authorizeMicrosoft')}
        border="rounded"
        size="small"
        disabled={oauth.email.trim() === ''}
        action={() => {
          const url = provider === EmailAccountTypes.GMAIL ? authGmailAccountURL : authMsAccountURL;
          window.open(url, '_blank');
        }}
      />

      {/* Row 3 – OAuth code */}
      <TextField
        label={t('fieldOauthCode')}
        value={oauth.code}
        onChange={(e) => setOauth({ ...oauth, code: e.target.value })}
        helperText={t('hintPasteOauth')}
        fullWidth
      />
    </Stack>
  );

  const credentialsStep = bulkMode ? (
    <Stack spacing={2} mt={2}>
      {!mappingComplete && (
        <>
          <Typography variant="subtitle1">{t('mapCsvColumns')}</Typography>
          {REQUIRED_FIELDS.map((field) => (
            <FormControl fullWidth key={field} sx={{ mb: 1 }}>
              <InputLabel>{field}</InputLabel>
              <Select
                label={field}
                value={columnMapping[field] || ''}
                onChange={(e) => setColumnMapping({ ...columnMapping, [field]: e.target.value })}
              >
                {headers.map((h) => (
                  <MenuItem value={h} key={h}>{h}</MenuItem>
                ))}
              </Select>
            </FormControl>
          ))}
          <Typography variant="body2" color="text.secondary">
            {t('completeMappingHint')}
          </Typography>
        </>
      )}
      {mappingComplete && (
        <>
          <Typography variant="body2" color="success.main">
            {t('csvLoadedSelected', { selected: selectedCount, count: csvRows.length })}
          </Typography>
          <Typography variant="body2" color="text.secondary">{t('selectRowsHint')}</Typography>

          {/* Preview table with same selection controls */}
          <TableContainer component={Paper} sx={{ maxHeight: 260, mt: 1 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={allSelected}
                      indeterminate={!allSelected && rowSelections.some((v) => v)}
                      onChange={toggleAllRows}
                    />
                  </TableCell>
                  {headers.map((h) => (
                    <TableCell key={h}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {csvRows.map((row, idx) => (
                  <TableRow key={idx} hover>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={rowSelections[idx]}
                        onChange={() => toggleRowSelection(idx)}
                      />
                    </TableCell>
                    {headers.map((h) => (
                      <TableCell key={h}>{row[h]}</TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {csvRows.length > 10 && (
            <Typography variant="caption" color="text.secondary">
              {t('tablePreviewHint', { shown: 10, total: csvRows.length })}
            </Typography>
          )}

          <Typography variant="body2" color="text.secondary" mt={1}>
            {t('clickNextToImport')}
          </Typography>
        </>
      )}
    </Stack>
  ) : provider === EmailAccountTypes.SMTP || provider === EmailAccountTypes.OTHER ? smtpCredentialsForm : oauthCredentialsForm;

  const summaryStep = bulkMode ? (
    <Stack spacing={2} mt={2}>
      <Typography variant="body2" color="success.main">
        {t('csvLoadedSelected', { selected: selectedCount, count: csvRows.length })}
      </Typography>
      <TableContainer component={Paper} sx={{ maxHeight: 260 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {headers.map((h) => (
                <TableCell key={h}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {selectedRows.map((row, idx) => (
              <TableRow key={idx} hover>
                {headers.map((h) => (
                  <TableCell key={h}>{row[h]}</TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  ) : (
    <Stack spacing={2} mt={2}>
      <Stack spacing={0.5}>
        <Typography>
          <strong>{t('provider')}:</strong> {t(`providerLabel_${(provider as string).toUpperCase()}` as any)}
        </Typography>
        {provider === EmailAccountTypes.SMTP || provider === EmailAccountTypes.OTHER ? (
          <>
            <Typography><strong>{t('smtpServer')}:</strong> {smtp.server}:{smtp.port} ({smtp.encryption})</Typography>
            <Typography><strong>{t('smtpUsername')}:</strong> {smtp.username}</Typography>
            <Typography><strong>{t('imapServer')}:</strong> {imap.server}:{imap.port} ({imap.encryption})</Typography>
            <Typography><strong>{t('imapUsername')}:</strong> {imap.username}</Typography>
          </>
        ) : (
          <Typography><strong>{t('fieldEmail')}:</strong> {oauth.email}</Typography>
        )}
      </Stack>
      {error && <Typography color="error">{error}</Typography>}
    </Stack>
  );

  const renderQuality = () => {
    if (!quality) return null;
    const entries: Array<[keyof Quality, boolean]> = [
      ['dmarc', !!quality.dmarc],
      ['spf', !!quality.spf],
      ['dkim', !!quality.dkim],
      ['mx', !!quality.mx],
      ['disposable', !quality.disposable], // disposable false = good
    ];
    return (
      <Stack>
        {entries.map(([key, ok]) => (
          <Stack direction="row" spacing={1} alignItems="center" key={key}>
            {ok ? <CheckIcon color="success" fontSize="small" /> : <ErrorIcon color="error" fontSize="small" />}
            <Typography textTransform="uppercase">{key}</Typography>
          </Stack>
        ))}
      </Stack>
    );
  };

  const verifyStep = (
    <Stack spacing={2} mt={2} alignItems="center">
      {loading && <CircularProgress />}

      {/* Single sender verification result */}
      {!loading && !bulkMode && verificationSuccess && (
        <>
          <Typography color="success.main" fontWeight={600}>
            {t('senderVerified')}
          </Typography>
          {renderQuality()}
        </>
      )}

      {!loading && !bulkMode && verificationSuccess === false && (
        <>
          <Typography color="error.main" fontWeight={600}>{error || t('verificationFailed')}</Typography>
          {renderQuality()}
          {errorCode === 'POOR_INBOX_QUALITY' && (
            <Typography variant="body2" color="text.secondary" textAlign="center" maxWidth={400}>
              {t('poorInboxHint')}
            </Typography>
          )}
        </>
      )}

      {/* Bulk import progress */}
      {!loading && bulkMode && (
        <>
          <Typography variant="subtitle2" gutterBottom>
            {t('inboxProgress', { index: bulkIdx + 1, total: selectedRows.length })}
          </Typography>
          {verificationSuccess !== null && (
            <>
              {currentEmail && (
                <Typography fontWeight={600}>{currentEmail}</Typography>
              )}
              <Typography fontWeight={600} color={verificationSuccess ? 'success.main' : 'warning.main'}>
                {verificationSuccess ? t('senderVerified') : error || t('verificationFailed')}
              </Typography>
              {renderQuality()}
              {!verificationSuccess && errorCode === 'POOR_INBOX_QUALITY' && (
                <Typography variant="body2" color="text.secondary" textAlign="center" maxWidth={400}>
                  {t('poorInboxHint')}
                </Typography>
              )}
            </>
          )}
          {bulkIdx === selectedRows.length && (
            <Typography fontWeight={600} color={importSummary.failed === 0 ? 'success.main' : 'warning.main'}>
              {t('importResult', { success: importSummary.success, failed: importSummary.failed })}
            </Typography>
          )}
        </>
      )}
    </Stack>
  );

  const renderStepContent = () => {
    switch (activeStep) {
      case WizardStep.PROVIDER:
        return providerStep;
      case WizardStep.CREDENTIALS:
        return credentialsStep;
      case WizardStep.SUMMARY:
        return summaryStep;
      case WizardStep.VERIFY:
        return verifyStep;
      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ p: 2 }}>
        <MuiStack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">{mode === 'add' ? t('addSender') : t('editSender')}</Typography>
          <PaiIconButton size="small" variant="regular" onClick={onClose} aria-label="close">
            <CloseIcon />
          </PaiIconButton>
        </MuiStack>
      </DialogTitle>
      <DialogContent dividers>
        <Stepper activeStep={activeStep} alternativeLabel>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {renderStepContent()}
      </DialogContent>
      <DialogActions>
        {activeStep > WizardStep.PROVIDER && (
          <PaiButton text={t('back')} border="rounded" size="small" action={back} />
        )}
        {/* Next button logic */}
        {bulkMode ? (
          activeStep === WizardStep.CREDENTIALS ? (
            <PaiButton text={t('next')} border="rounded" size="small" action={() => setActiveStep(WizardStep.SUMMARY)} />
          ) : activeStep === WizardStep.SUMMARY ? (
            <PaiButton text={t('next')} border="rounded" size="small" action={() => setActiveStep(WizardStep.VERIFY)} />
          ) : activeStep === WizardStep.VERIFY && bulkIdx < selectedRows.length - 1 ? (
            <PaiButton text={t('nextInbox')} border="rounded" size="small" action={() => {
              setVerificationSuccess(null);
              setError(null);
              setQuality(null);
              setBulkIdx((prev) => prev + 1);
              setErrorCode(null);
            }} />
          ) : null
        ) : (
          <>
            {activeStep < WizardStep.SUMMARY && (
              <PaiButton text={t('next')} border="rounded" size="small" action={next} />
            )}
            {activeStep === WizardStep.SUMMARY && (
              <PaiButton text={t('next')} border="rounded" size="small" action={() => setActiveStep(WizardStep.VERIFY)} />
            )}
          </>
        )}
        {activeStep === WizardStep.VERIFY && verificationSuccess && (
          <PaiButton text={t('finish')} border="rounded" size="small" action={onCompleted} />
        )}
        {activeStep === WizardStep.VERIFY && verificationSuccess === false && (
          <PaiButton text={t('back')} border="rounded" size="small" action={() => setActiveStep(WizardStep.CREDENTIALS)} />
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AddEditSenderWizard;